import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MapMarker, MapConfig } from '../../@shared/components/location-map/location-map';
import { CompanyService } from '../../@shared/services/company.service';
import { TrainerService } from '../../@shared/services/trainer.service';
import { GeocodingService } from '../../@shared/services/geocoding.service';
import { EndpointService } from '../../@shared/endpoints.service';
import { CompanyResponse, TrainerResponse, CompanyCategoryResponse } from '../../@shared/models/response.module';
import { AuthService } from '../../@core/services/auth.service';

@Component({
  selector: 'app-home',
  standalone: false,
  templateUrl: './home.html',
  styleUrl: './home.css'
})
export class Home implements OnInit {
  activeMapType: string | null = null;
  currentMarkers: MapMarker[] = [];
  companies: CompanyResponse[] = [];
  trainers: TrainerResponse[] = [];
  categories: CompanyCategoryResponse[] = [];
  geocodingInProgress: boolean = false;

  // Store service category types and their category keys
  private storeServiceTypes = ['horse_care', 'equipment_tack'];
  private storeServiceCategoryKeys: { [key: string]: string[] } = {
    'horse_care': ['farrier', 'horse_massage', 'veterinarian', 'horse_dentist', 'horse_chiropractor'],
    'equipment_tack': ['riding_store', 'riding_equipment', 'stable_equipment', 'horse_feed']
  };

  mapConfig: MapConfig = {
    height: '500px',
    mobileHeight: '400px',
    defaultCenter: { lat: 59.9139, lng: 10.7522 },
    defaultZoom: 6,
    enableClustering: true
  };

  constructor(
    private router: Router,
    private companyService: CompanyService,
    private trainerService: TrainerService,
    private geocodingService: GeocodingService,
    private endpointService: EndpointService,
    public authService: AuthService
  ) {}

  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  ngOnInit() {
    this.loadCompanies();
    this.loadTrainers();
    this.loadCategories();
  }

  loadCategories() {
    this.endpointService.cor_coy_32().subscribe({
      next: (res) => {
        this.categories = res.data || [];
      }
    });
  }

  loadCompanies() {
    this.companyService.getPublicCompanies({}).subscribe({
      next: (response) => {
        this.companies = response.data || [];
      }
    });
  }

  loadTrainers() {
    this.trainerService.getTrainers({ per_page: 100 }).subscribe({
      next: (response) => {
        this.trainers = response.data || [];
      }
    });
  }

  showMap(type: string) {
    // Navigate to the appropriate page with filter instead of showing map
    this.navigateToCommunity(type);
  }

  navigateToCommunity(type: string) {
    if (type === 'trainer') {
      this.router.navigate(['/trainers']);
    } else if (this.storeServiceTypes.includes(type)) {
      // For horse_care and equipment_tack, go to stores-services page
      // These are category groups, so users can filter on the page
      this.router.navigate(['/stores-services']);
    } else {
      // For stable, riding_club, etc., go to organisations with type filter
      this.router.navigate(['/organisations'], { queryParams: { type: type } });
    }
  }

  hideMap() {
    this.activeMapType = null;
    this.currentMarkers = [];
  }

  async geocodeCompanies(type: string) {
    this.geocodingInProgress = true;
    this.currentMarkers = [];

    // Filter companies by type
    const filteredCompanies = this.companies.filter(
      company => company.company_type === type
    );

    for (const company of filteredCompanies) {
      // Skip if no address info
      if (!company.address && !company.city && !company.country) {
        continue;
      }

      const result = await this.geocodingService.geocodeAddress(
        company.address || '',
        company.city || '',
        company.postal_code || '',
        company.country || ''
      );

      if (result.success && result.lat && result.lng) {
        this.currentMarkers = [...this.currentMarkers, {
          id: company.company_id,
          lat: result.lat,
          lng: result.lng,
          title: company.company_name || 'Unknown',
          subtitle: company.city || '',
          imageUrl: company.company_logo_url || undefined,
          data: company
        }];
      }
    }

    this.geocodingInProgress = false;
  }

  async geocodeTrainers() {
    this.geocodingInProgress = true;
    this.currentMarkers = [];

    const trainersToGeocode = this.trainers.filter(
      trainer => trainer.address || trainer.city || trainer.country
    );

    for (const trainer of trainersToGeocode) {
      const result = await this.geocodingService.geocodeAddress(
        trainer.address || '',
        trainer.city || '',
        trainer.postal_code || '',
        trainer.country || ''
      );

      if (result.success && result.lat && result.lng) {
        this.currentMarkers = [...this.currentMarkers, {
          id: trainer.user_id,
          lat: result.lat,
          lng: result.lng,
          title: trainer.full_name || 'Unknown Trainer',
          subtitle: this.getTrainerLocation(trainer),
          imageUrl: trainer.profile_image_url || undefined,
          data: trainer
        }];
      }
    }

    this.geocodingInProgress = false;
  }

  async geocodeStoreServices(mapType: string) {
    this.geocodingInProgress = true;
    this.currentMarkers = [];

    // Get category keys for this map type
    const categoryKeys = this.storeServiceCategoryKeys[mapType] || [];

    // Find matching category IDs
    const categoryIds = this.categories
      .filter(cat => categoryKeys.includes(cat.category_key || ''))
      .map(cat => cat.category_id);

    // Filter stores_services companies by category IDs
    const filteredCompanies = this.companies.filter(company => {
      if (company.company_type !== 'stores_services') return false;
      if (categoryIds.length > 0 && !categoryIds.includes(company.category_id!)) return false;
      return true;
    });

    for (const company of filteredCompanies) {
      if (!company.address && !company.city && !company.country) {
        continue;
      }

      const result = await this.geocodingService.geocodeAddress(
        company.address || '',
        company.city || '',
        company.postal_code || '',
        company.country || ''
      );

      if (result.success && result.lat && result.lng) {
        this.currentMarkers = [...this.currentMarkers, {
          id: company.company_id,
          lat: result.lat,
          lng: result.lng,
          title: company.company_name || 'Unknown',
          subtitle: company.city || '',
          imageUrl: company.company_logo_url || undefined,
          data: { ...company, isStoreService: true }
        }];
      }
    }

    this.geocodingInProgress = false;
  }

  private getTrainerLocation(trainer: TrainerResponse): string {
    const parts = [trainer.city, trainer.country].filter(p => p);
    return parts.join(', ');
  }

  onMarkerClick(marker: MapMarker) {
    // Could scroll to a card or highlight
  }

  onMarkerAction(marker: MapMarker) {
    // Navigate based on type
    if (this.activeMapType === 'trainer') {
      this.router.navigate(['/trainers', marker.id]);
    } else if (this.storeServiceTypes.includes(this.activeMapType || '')) {
      this.router.navigate(['/stores-services', marker.id]);
    } else {
      this.router.navigate(['/organisations', marker.id]);
    }
  }

  navigateToStoresServices(category?: string) {
    if (category) {
      this.router.navigate(['/stores-services'], { queryParams: { category } });
    } else {
      this.router.navigate(['/stores-services']);
    }
  }
}
